import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { INCAPACITATION, SIP_TYPE_VALUE } from "../../constants";
import { NomineeDetailsForm } from "../../types/createNewFolio";
import { FundDetails, MODES_OF_INVESTMENT } from "../../types/invest";

export interface InvestmentDetailsFormValues {
  lumpsumAmount: number | undefined;
  payFirstInstallment: boolean;
  sipAmount: number | undefined;
  installments: number | undefined;
  sipFrequency: string;
  sipType: SIP_TYPE_VALUE;
  sipStartDate: Date | null;
  modeOfInvestment: MODES_OF_INVESTMENT;
  riaCode?: string;
  isRiaValid?: boolean;
  arnCode?: string;
  isArnValid?: boolean;
  subBroker?: string;
  subBrokerArn?: string;
  euin?: string;
  isEuinValid?: boolean;
  isEuinDeclaration?: boolean;
  option: string;
  schemeName: string;
  selectedInvestTab?: string;
  isEnachMandateFlow?: boolean;
  paymentMode?: string;
  selectedOption?: string;
  dematOption?: "NON_DEMAT" | "DEMAT";
  dpType?: "NSDL" | "CDSL";
  cdslDematNo?: string;
  nsdlDpId?: string;
  nsdlClientId?: string;
  dematDpId?: string;
  dematClientId?: string;
  referenceNumber?: string;
  folioNumber?: string;
}

import { Applicant, JointHolder, MinorDetails } from "../../types";

export interface ApplicantFormValues {
  applicant: Omit<Applicant, "dob"> & { dob: string };
  jointHolders: (Omit<JointHolder, "dob"> & { dob: string })[];
  // Minor details (only populated for Minor account holder type).
  // Uses the shared MinorDetails type so document arrays (age/relation proof,
  // cancelled cheque) are typed rather than cast to `any` at the use sites.
  minorDetails?: MinorDetails;
}

export interface KYCFormValues {
  income: string;
  occupation: string;
  isTaxResident: boolean;
  isNotPEP: boolean;
  countryOfTaxResidency: string;
  foreignTaxIdType: string;
  foreignTaxId: string;
  // city: string;
  placeOfBirth: string;
  isTinValidated: boolean;
  countryOfBirth: string;
  countryOfCitizenship: string;
  countryOfCitizenShip: string;
  countryOfResidence: string;
  countryOfTaxResident: string;
  countryCode: string;
  nationality: string;
}

export interface ReduxNomineeDetailsFormValues {
  nomineeDetails: NomineeDetailsForm[];
  isIncapacitation: boolean;
  incapacitationType: string;
  incapacitationValue: string;
  incapacitationNominee: string;
  SOAFlag: "Y" | "N";
  doNotWishToNominate: boolean;
}

interface BankDetailsFormValues {
  bankVerificationToken?: any;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  accountType?: string;
  city?: string;
  address?: string;
  pincode?: number;
  verificationResponse?: Record<string, unknown>;
  isDefault?: boolean | string;
  bnkactype?: string;
  /** Opaque server response from the minor document upload, threaded into the
   *  checkout payload. The structure varies per API version so we keep it
   *  permissive while still eliminating `any`. */
  uploadCheckResponse?: Record<string, unknown>;
}

interface InvestmentFormState {
  investmentDetails: InvestmentDetailsFormValues;
  applicantDetails: ApplicantFormValues;
  kycDetails: KYCFormValues[];
  nomineeDetails: ReduxNomineeDetailsFormValues;
  bankDetails: BankDetailsFormValues;
  fundDetails: {
    fromSchemeDetails: any;
    toSchemeDetails: FundDetails | null;
  };
  accountHolderType: string;
  /** When true, Invest suppresses the "plan changed to Regular" info toast.
   *  Set at navigation time by the Portfolio/Dashboard "Transact" dropdowns;
   *  cleared when a fund is picked from Explore. Persisted so it survives a
   *  web refresh. */
  suppressPlanToast?: boolean;
}

const initialState: InvestmentFormState = {
  investmentDetails: {
    lumpsumAmount: undefined,
    payFirstInstallment: false,
    sipAmount: undefined,
    installments: undefined,
    sipFrequency: "",
    sipType: SIP_TYPE_VALUE.NUMBER_OF_INSTALLMENTS,
    sipStartDate: null,
    modeOfInvestment: MODES_OF_INVESTMENT.DIRECT,
    riaCode: "",
    isRiaValid: false,
    arnCode: "",
    isArnValid: false,
    subBroker: "",
    subBrokerArn: "",
    euin: "",
    isEuinValid: false,
    isEuinDeclaration: false,
    option: "",
    schemeName: "",
    isEnachMandateFlow: false,
    paymentMode: "",
    selectedOption: "",
    referenceNumber: "",
  },
  applicantDetails: {
    applicant: {
      name: "",
      dob: "",
      gender: "",
      category: "",
    },
    jointHolders: [],
  },
  kycDetails: [
    {
      income: "",
      occupation: "",
      isTaxResident: true,
      isNotPEP: true,
      countryOfTaxResidency: "",
      foreignTaxIdType: "",
      foreignTaxId: "",
      // city: "",
      isTinValidated: false,
      countryOfBirth: "",
      countryOfCitizenship: "",
      countryOfCitizenShip: "",
      countryOfResidence: "",
      countryOfTaxResident: "",
      countryCode: "",
      nationality: "",
      placeOfBirth: "",
    },
  ],
  nomineeDetails: {
    isIncapacitation: false,
    incapacitationType: INCAPACITATION.AMOUNT,
    incapacitationValue: "",
    incapacitationNominee: "",
    SOAFlag: "Y",
    nomineeDetails: [],
    doNotWishToNominate: false,
  },
  bankDetails: {
    bankName: "",
    ifscCode: "",
    accountNumber: "",
    bankVerificationToken: "",
  },
  fundDetails: {
    fromSchemeDetails: null,
    toSchemeDetails: null,
  },
  accountHolderType: "",
  suppressPlanToast: false,
};

const investmentFormSlice = createSlice({
  name: "investmentForm",
  initialState,
  reducers: {
    saveAllInvestmentData: (state, action: PayloadAction<InvestmentFormState>) => ({
      ...state,
      ...action.payload,
    }),
    saveInvestmentDetails: (
      state,
      action: PayloadAction<InvestmentDetailsFormValues>,
    ) => {
      state.investmentDetails = action.payload;
    },
    saveApplicantDetails: (state, action: PayloadAction<ApplicantFormValues>) => {
      state.applicantDetails = action.payload;
    },
    saveKycDetails: (state, action: PayloadAction<KYCFormValues[]>) => {
      state.kycDetails = action.payload;
    },
    saveNomineeDetails: (state, action: PayloadAction<ReduxNomineeDetailsFormValues>) => {
      state.nomineeDetails = action.payload;
    },
    saveBankDetails: (state, action: PayloadAction<BankDetailsFormValues>) => {
      state.bankDetails = action.payload;
    },
    saveFundDetails: (
      state,
      action: PayloadAction<{
        fromSchemeDetails?: any;
        toSchemeDetails?: FundDetails | null;
      }>,
    ) => {
      state.fundDetails = {
        fromSchemeDetails: action.payload.fromSchemeDetails || null,
        toSchemeDetails: action.payload.toSchemeDetails ?? null,
      };
    },
    saveAccountHolderType: (state, action: PayloadAction<string>) => {
      state.accountHolderType = action.payload;
    },
    setSuppressPlanToast: (state, action: PayloadAction<boolean>) => {
      state.suppressPlanToast = action.payload;
    },
    patchInvestmentDetails: (
      state,
      action: PayloadAction<Partial<InvestmentDetailsFormValues>>,
    ) => {
      state.investmentDetails = { ...state.investmentDetails, ...action.payload };
    },
    resetInvestmentForm: () => initialState,
  },
});

export const { actions: investmentFormActions, reducer: investmentFormReducer } =
  investmentFormSlice;

export const {
  saveApplicantDetails,
  saveKycDetails,
  saveNomineeDetails,
  saveBankDetails,
  saveFundDetails,
  setSuppressPlanToast,
  resetInvestmentForm,
} = investmentFormSlice.actions;
